"use client"

import { useEffect, useRef, useState } from "react"

type VisibilityState = "closed" | "opening" | "open" | "closing"

type UseAnchoredVisibilityOptions = {
  animationMs?: number
  getAnchorRect: (target?: EventTarget | null) => DOMRect | null
}

export function useAnchoredVisibility({
  animationMs = 280,
  getAnchorRect,
}: UseAnchoredVisibilityOptions) {
  const [state, setState] = useState<VisibilityState>("closed")
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null)
  const timerRef = useRef<number | null>(null)

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current)
      }
    }
  }, [])

  const clearTimer = () => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }

  const open = (target?: EventTarget | null) => {
    clearTimer()
    setAnchorRect((prev) => getAnchorRect(target) ?? prev)
    setState("opening")
    window.requestAnimationFrame(() => {
      setState("open")
    })
  }

  const close = (target?: EventTarget | null) => {
    clearTimer()
    setAnchorRect((prev) => getAnchorRect(target) ?? prev)
    setState("closing")
    timerRef.current = window.setTimeout(() => {
      setState("closed")
      timerRef.current = null
    }, animationMs)
  }

  const toggle = (target?: EventTarget | null) => {
    if (state === "open" || state === "opening") {
      close(target)
      return
    }

    open(target)
  }

  return {
    state,
    anchorRect,
    isMounted: state !== "closed",
    isVisible: state === "open",
    open,
    close,
    toggle,
  }
}
